/**
 * Getting a usable object out of whatever the model actually said.
 *
 * Llama 3.2 vision replies with the JSON it was asked for most of the time, and
 * the rest of the time with a code fence around it, a sentence in front of it, a
 * sign-off after it, or all three. Everything here exists to survive that.
 */

import { FOOD_CATEGORIES } from "../food/types";
import type { FoodCategory } from "../food/types";
import type { Recognition } from "./types";

const MAX_NAME_LENGTH = 60;
const MIN_SHELF_LIFE_DAYS = 1;
const MAX_SHELF_LIFE_DAYS = 3650;

/**
 * Words a model reaches for that aren't in our seven. Anything unmapped becomes
 * "other" rather than being rejected — a right name with a vague category still
 * saves the user typing.
 */
const CATEGORY_SYNONYMS: Record<string, FoodCategory> = {
  fruit: "produce", fruits: "produce", vegetable: "produce", vegetables: "produce",
  veg: "produce", greens: "produce", herb: "produce", herbs: "produce",
  salad: "produce", citrus: "produce",
  fish: "meat", seafood: "meat", poultry: "meat", protein: "meat", deli: "meat",
  chicken: "meat", beef: "meat", pork: "meat",
  cheese: "dairy", eggs: "dairy", egg: "dairy", milk: "dairy", yoghurt: "dairy",
  yogurt: "dairy", butter: "dairy",
  bakery: "pantry", bread: "pantry", grain: "pantry", grains: "pantry",
  "dry goods": "pantry", drygoods: "pantry",
  canned: "pantry", tinned: "pantry", condiment: "pantry", condiments: "pantry",
  snack: "pantry", snacks: "pantry", spice: "pantry", spices: "pantry",
  pasta: "pantry", cereal: "pantry", baking: "pantry",
  "frozen food": "frozen", "frozen goods": "frozen",
  icecream: "frozen", "ice cream": "frozen",
  beverage: "drinks", beverages: "drinks", drink: "drinks", juice: "drinks",
  water: "drinks", soda: "drinks", coffee: "drinks", tea: "drinks",
};

export function toCategory(raw: unknown): FoodCategory {
  if (typeof raw !== "string") return "other";

  // Underscores and hyphens become spaces so "dry_goods" and "dry-goods" both
  // land on the same key as "dry goods".
  const key = raw.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  if ((FOOD_CATEGORIES as readonly string[]).includes(key)) return key as FoodCategory;
  if (CATEGORY_SYNONYMS[key]) return CATEGORY_SYNONYMS[key];

  // "fresh vegetables", "dairy products" — try the individual words before
  // giving up, since a partial match still beats "other".
  for (const word of key.split(" ")) {
    if ((FOOD_CATEGORIES as readonly string[]).includes(word)) return word as FoodCategory;
    if (CATEGORY_SYNONYMS[word]) return CATEGORY_SYNONYMS[word];
  }

  return "other";
}

/**
 * Finds the first balanced `{...}` in a string, ignoring braces inside string
 * literals.
 *
 * `indexOf("{")` plus `lastIndexOf("}")` is the obvious approach and it breaks
 * on a reply containing two objects, or a brace inside a name. This scans.
 */
export function extractJsonObject(text: string): string | null {
  let depth = 0;
  let start = -1;
  let opener = "";
  let inString = false;
  let escaped = false;

  // Brackets as well as braces, since asking for a list gets a list back — and
  // a fenced `[...]` was previously invisible to this scanner.
  const CLOSERS: Record<string, string> = { "{": "}", "[": "]" };

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (inString) {
      if (escaped) escaped = false;
      else if (char === "\\") escaped = true;
      else if (char === '"') inString = false;
      continue;
    }

    if (char === '"') {
      inString = true;
    } else if (char === "{" || char === "[") {
      if (depth === 0) {
        start = i;
        opener = char;
      }
      // Only count the kind we opened with, so an object inside an array
      // doesn't close the array early and vice versa.
      if (char === opener) depth++;
    } else if (char === CLOSERS[opener]) {
      depth--;
      if (depth === 0 && start !== -1) return text.slice(start, i + 1);
      if (depth < 0) depth = 0; // a stray closer in prose
    }
  }

  return null;
}

/** JSON.parse first; failing that, the first balanced object in the reply. */
export function parseLoosely(text: string): unknown {
  const trimmed = text.trim();

  try {
    return JSON.parse(trimmed);
  } catch {
    // Fenced, prefaced, or followed by chatter — fall through to the scanner.
  }

  const candidate = extractJsonObject(trimmed);
  if (!candidate) return null;

  try {
    return JSON.parse(candidate);
  } catch {
    return null;
  }
}

/** Control characters and stray whitespace that turn up inside model strings. */
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;

/** A day count from a number or a phrase, or null when there is no number in it. */
function readDays(raw: unknown): number | null {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== "string") return null;

  const digits = raw.replace(/[^0-9.-]/g, "");
  if (!/\d/.test(digits)) return null;

  const value = Number(digits);
  return Number.isFinite(value) ? value : null;
}

/** Turns whatever came back into a Recognition, or null if it's unusable. */
export function toRecognition(payload: unknown): Recognition | null {
  if (typeof payload !== "object" || payload === null) return null;
  const record = payload as Record<string, unknown>;

  // Models drift between snake_case, camelCase and the bare word.
  const rawName = record.item_name ?? record.itemName ?? record.name;
  const name =
    typeof rawName === "string"
      ? rawName.replace(CONTROL_CHARS, " ").replace(/\s+/g, " ").trim()
      : "";
  // A nameless result saves nobody any typing, so it counts as a failure.
  if (!name || name.length > MAX_NAME_LENGTH) return null;

  const rawDays =
    record.estimated_shelf_life_days ??
    record.estimatedShelfLifeDays ??
    record.shelf_life_days;
  // "about 7 days" and "7" should both work; "soon" should not. Stripping the
  // non-numeric characters from "soon" leaves an empty string, and Number("")
  // is 0 — which would quietly become a one-day shelf life after clamping.
  const days = readDays(rawDays);
  if (days === null) return null;

  return {
    name,
    category: toCategory(record.category),
    shelfLifeDays: Math.min(
      MAX_SHELF_LIFE_DAYS,
      Math.max(MIN_SHELF_LIFE_DAYS, Math.round(days))
    ),
  };
}

/**
 * Everything usable in a reply, in the order the model listed it.
 *
 * Accepts the three shapes that actually come back when you ask for a list: a
 * bare array, `{"items": [...]}`, and — because a model asked for a list of one
 * thing will often just send the thing — a single object.
 *
 * Duplicates are dropped. A shelf photo reliably produces "milk" twice when two
 * cartons are visible, and adding the same item twice is worse than missing one.
 */
export function toRecognitions(payload: unknown): Recognition[] {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { items?: unknown })?.items)
      ? (payload as { items: unknown[] }).items
      : [payload];

  const seen = new Set<string>();
  const results: Recognition[] = [];
  for (const entry of list) {
    const result = toRecognition(entry);
    if (!result) continue;
    const key = result.name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    results.push(result);
  }
  return results;
}
